import {
  DuplexMode,
  FrequencyRange,
  FronthaulType,
  LicenseDto,
  LicenseStatus,
  SimpleMessageService,
  WebAppCommonModule,
} from "@allbesmart/web-app-common";
import { Component, OnDestroy } from "@angular/core";
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { Router } from "@angular/router";
import { Subscription } from "rxjs";
import { AvailableRoutes } from "src/app/enums/available-routes";
import { CreateTenantDto } from "../../models/tenant/create-tenant-dto";
import { TenantManagementService } from "../../services/tenant-management.service";
import {
  DuplexModeFormGroup,
  FrequencyRangeFormGroup,
  FronthaulTypeFormGroup,
  NewDeploymentFormGroup,
} from "./new-deployment-page-form";

const UUID_PATTERN =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

@Component({
  selector: "oaibox-new-deployment-page",
  templateUrl: "./new-deployment-page.component.html",
  styleUrls: ["./new-deployment-page.component.css"],
  imports: [WebAppCommonModule, ReactiveFormsModule],
})
export class NewDeploymentPageComponent implements OnDestroy {
  protected readonly AVAILABLE_LICENSE_STATUS = Object.values(LicenseStatus);

  protected newDeploymentForm: NewDeploymentFormGroup;
  private newDeploymentSubscription?: Subscription;

  protected ongoingRequest: boolean = false;

  constructor(
    private readonly tenantManagementService: TenantManagementService,
    private readonly simpleMessageService: SimpleMessageService,
    private readonly router: Router
  ) {
    this.newDeploymentForm = this.getNewDeploymentForm();
  }

  private getNewDeploymentForm(): NewDeploymentFormGroup {
    const today = new Date().toISOString().slice(0, 10); // "YYYY-MM-DD"

    return new FormGroup({
      name: new FormControl<string | null>(null, [Validators.required]),
      description: new FormControl<string | null>(null, [Validators.required]),
      domain: new FormControl<string | null>(null, [Validators.required]),
      domainAutoAssignment: new FormControl<boolean>(true),
      licenseConfiguration: new FormGroup({
        status: new FormControl<LicenseStatus | null>(
          LicenseStatus.ACTIVE,
          Validators.required
        ),
        startDate: new FormControl<string | null>(today, Validators.required),
        endDate: new FormControl<string | null>(today),
      }),
      offlineConfiguration: new FormGroup({
        enabled: new FormControl<boolean>(false),
      }),
      fronthaulTypes: new FormArray(
        Object.values(FronthaulType)
          .filter((fronthaul) => fronthaul !== FronthaulType.NONE)
          .map(
            (fronthaul) =>
              new FormGroup({
                checked: new FormControl<boolean>(
                  fronthaul === FronthaulType.SPLIT_8
                ),
                type: new FormControl<FronthaulType>(fronthaul),
              }) as FronthaulTypeFormGroup
          )
      ),

      frequencyRanges: new FormArray(
        Object.values(FrequencyRange).map(
          (frequencyRange) =>
            new FormGroup({
              checked: new FormControl<boolean>(
                frequencyRange === FrequencyRange.FR1
              ),
              type: new FormControl<FrequencyRange>(frequencyRange),
            }) as FrequencyRangeFormGroup
        )
      ),

      duplexModes: new FormArray(
        Object.values(DuplexMode).map(
          (duplexMode) =>
            new FormGroup({
              checked: new FormControl<boolean>(duplexMode === DuplexMode.TDD),
              type: new FormControl<DuplexMode>(duplexMode),
            }) as DuplexModeFormGroup
        )
      ),

      education: new FormControl<boolean>(false),
      kubernetes: new FormControl<boolean>(false),
      advancedMode: new FormControl<boolean>(false),

      // Features
      flexRic: new FormControl<boolean>(true),
      ueManagement: new FormGroup({
        enabled: new FormControl<boolean>(false),
        oaiUeMachineIds: new FormArray<FormControl<string | null>>([]),
      }),
      networkSlicing: new FormControl<boolean>(false),

      // Premium Features
      mobility: new FormControl<boolean>(false),
      qosium: new FormControl<boolean>(false),
      ris: new FormControl<boolean>(false),
      multiRan: new FormControl<boolean>(false),
      redCapConfiguration: new FormGroup({
        redCap: new FormControl<boolean>(false),
        halfDuplexRedCapAllowedR17: new FormControl<number>(0),
        cellBarredRedCap1RxR17: new FormControl<number>(1),
        cellBarredRedCap2RxR17: new FormControl<number>(1),
        intraFreqReselectionRedCapR17: new FormControl<number>(0),
        t300: new FormControl<number>(400),
        t301: new FormControl<number>(400),
        t310: new FormControl<number>(2000),
        t311: new FormControl<number>(3000),
        n310: new FormControl<number>(20),
        n311: new FormControl<number>(1),
        t319: new FormControl<number>(400),
      }),

      // Use Cases Library
      digitalTwin: new FormControl<boolean>(false),
      isac: new FormControl<boolean>(false),
      radarDetection: new FormControl<boolean>(false),
      visionAidedCommunications: new FormControl<boolean>(false),
    });
  }

  public checkForAvailableFronthaulTypes(): FronthaulType[] {
    const availableFronthaulType =
      this.newDeploymentForm.controls.fronthaulTypes.controls
        .filter((fronthaul) => fronthaul.value.checked)
        .map((fronthaul) => fronthaul.value.type);

    return availableFronthaulType.length > 0
      ? (availableFronthaulType as FronthaulType[])
      : [FronthaulType.SPLIT_8];
  }

  public checkForAvailableFrequencyRanges(): FrequencyRange[] {
    const availableFrequencyRanges =
      this.newDeploymentForm.controls.frequencyRanges.controls
        .filter((frequencyRange) => frequencyRange.value.checked)
        .map((frequencyRange) => frequencyRange.value.type);

    return availableFrequencyRanges.length > 0
      ? (availableFrequencyRanges as FrequencyRange[])
      : [FrequencyRange.FR1];
  }

  public checkForAvailableDuplexModes(): DuplexMode[] {
    const availableDuplexModes =
      this.newDeploymentForm.controls.duplexModes.controls
        .filter((duplexMode) => duplexMode.value.checked)
        .map((duplexMode) => duplexMode.value.type);

    return availableDuplexModes.length > 0
      ? (availableDuplexModes as DuplexMode[])
      : [DuplexMode.TDD];
  }

  protected toggleOffline(): void {
    const enabled =
      this.newDeploymentForm.controls.offlineConfiguration.controls.enabled;
    enabled.patchValue(!enabled.value);
  }

  protected toggleUeManagement(): void {
    const ueManagement = this.newDeploymentForm.controls.ueManagement;
    const enabled = ueManagement.controls.enabled;
    enabled.patchValue(!enabled.value);
    if (!enabled.value) {
      ueManagement.controls.oaiUeMachineIds.clear();
    } else if (ueManagement.controls.oaiUeMachineIds.length === 0) {
      this.addOaiUeMachineId();
    }
  }

  protected addOaiUeMachineId(): void {
    this.newDeploymentForm.controls.ueManagement.controls.oaiUeMachineIds.push(
      new FormControl<string | null>(null, [Validators.pattern(UUID_PATTERN)])
    );
  }

  protected removeOaiUeMachineId(index: number): void {
    this.newDeploymentForm.controls.ueManagement.controls.oaiUeMachineIds.removeAt(
      index
    );
  }

  protected onSelectLicenseStatus(licenseStatus: LicenseStatus): void {
    this.newDeploymentForm.controls.licenseConfiguration.controls.status.patchValue(
      licenseStatus
    );

    const endDateFormControl =
      this.newDeploymentForm.controls.licenseConfiguration.controls.endDate;
    if (licenseStatus === LicenseStatus.PERPETUAL) endDateFormControl.disable();
    else endDateFormControl.enable();
  }

  protected onSelectFronthaulType(fronthaulType: FronthaulTypeFormGroup): void {
    const value = this.newDeploymentForm.controls.fronthaulTypes.controls.find(
      (type) => type === fronthaulType
    )?.value.checked;

    fronthaulType.controls.checked.patchValue(!value);
    this.checkForAvailableFronthaulTypes();
  }

  protected onSelectFrequencyRange(
    frequencyRange: FrequencyRangeFormGroup
  ): void {
    const value = this.newDeploymentForm.controls.frequencyRanges.controls.find(
      (range) => range === frequencyRange
    )?.value.checked;

    frequencyRange.controls.checked.patchValue(!value);
    this.checkForAvailableFrequencyRanges();
  }

  protected onSelectDuplexMode(duplexMode: DuplexModeFormGroup): void {
    const value = this.newDeploymentForm.controls.duplexModes.controls.find(
      (mode) => mode === duplexMode
    )?.value.checked;

    duplexMode.controls.checked.patchValue(!value);
    this.checkForAvailableDuplexModes();
  }

  public createDeployment(): void {
    if (this.newDeploymentForm.invalid) return;

    const availableFronthaulType = this.checkForAvailableFronthaulTypes();
    const availableFrequencyRanges = this.checkForAvailableFrequencyRanges();
    const availableDuplexModes = this.checkForAvailableDuplexModes();

    const licenseConfigurationForm =
      this.newDeploymentForm.controls.licenseConfiguration.value;
    const startDate = licenseConfigurationForm.startDate;
    const endDate = licenseConfigurationForm.endDate;

    let startTimestamp: number | undefined;
    let endTimestamp: number | undefined;
    if (startDate) startTimestamp = new Date(startDate).getTime();
    if (endDate) endTimestamp = new Date(endDate).getTime();

    const licenseDto: LicenseDto = {
      status:
        this.newDeploymentForm.controls.licenseConfiguration.value.status ??
        undefined,
      startTimestamp: startTimestamp,
      endTimestamp: endTimestamp,
    };

    const newDeployment: CreateTenantDto = {
      clientName: this.newDeploymentForm.value.name!,
      clientDomain: this.newDeploymentForm.value.domain!,
      clientDescription: this.newDeploymentForm.value.description ?? "",
      license: licenseDto,
      offline: {
        enable:
          this.newDeploymentForm.controls.offlineConfiguration.value.enabled ??
          false,
      },
      domainAutoAssignmentEnabled:
        this.newDeploymentForm.value.domainAutoAssignment ?? true,
      availableFronthaulTypes: availableFronthaulType,
      availableFrequencyRanges: availableFrequencyRanges,
      availableDuplexModes: availableDuplexModes,

      education: this.newDeploymentForm.value.education ?? false,
      kubernetes: this.newDeploymentForm.value.kubernetes ?? false,
      advancedMode: this.newDeploymentForm.value.advancedMode ?? false,
      advancedModeConfiguration: {
        enable: false,
      },

      flexRic: this.newDeploymentForm.value.flexRic ?? false,
      ueManagement: {
        enabled:
          this.newDeploymentForm.controls.ueManagement.controls.enabled.value ??
          false,
        oaiUeMachineIds:
          this.newDeploymentForm.controls.ueManagement.controls.oaiUeMachineIds.controls
            .map((c) => c.value)
            .filter((v): v is string => !!v),
      },
      slicing: this.newDeploymentForm.value.networkSlicing ?? false,

      handover: this.newDeploymentForm.value.mobility ?? false,
      qosium: this.newDeploymentForm.value.qosium ?? false,
      ris: this.newDeploymentForm.value.ris ?? false,
      multiRan: this.newDeploymentForm.value.multiRan ?? false,
      redCap:
        this.newDeploymentForm.controls.redCapConfiguration.value.redCap ??
        false,

      digitalTwin: this.newDeploymentForm.value.digitalTwin ?? false,
      isac: this.newDeploymentForm.value.isac ?? false,
      radarDetection: this.newDeploymentForm.value.radarDetection ?? false,
      visionAidedCommunications:
        this.newDeploymentForm.value.visionAidedCommunications ?? false,
    };

    if (newDeployment.redCap) {
      newDeployment.redCapConfiguration = {
        enable: newDeployment.redCap,
        halfDuplexRedCapAllowedR17:
          this.newDeploymentForm.controls.redCapConfiguration.value
            .halfDuplexRedCapAllowedR17 ?? 0,
        cellBarredRedCap1RxR17:
          this.newDeploymentForm.controls.redCapConfiguration.value
            .cellBarredRedCap1RxR17 ?? 1,
        cellBarredRedCap2RxR17:
          this.newDeploymentForm.controls.redCapConfiguration.value
            .cellBarredRedCap2RxR17 ?? 1,
        intraFreqReselectionRedCapR17:
          this.newDeploymentForm.controls.redCapConfiguration.value
            .intraFreqReselectionRedCapR17 ?? 0,
      };

      newDeployment.timersConfiguration = {
        t300:
          this.newDeploymentForm.controls.redCapConfiguration.value.t300 ?? 400,
        t301:
          this.newDeploymentForm.controls.redCapConfiguration.value.t301 ?? 400,
        t310:
          this.newDeploymentForm.controls.redCapConfiguration.value.t310 ??
          2000,
        t311:
          this.newDeploymentForm.controls.redCapConfiguration.value.t311 ??
          3000,
        n310:
          this.newDeploymentForm.controls.redCapConfiguration.value.n310 ?? 20,
        n311:
          this.newDeploymentForm.controls.redCapConfiguration.value.n311 ?? 1,
        t319:
          this.newDeploymentForm.controls.redCapConfiguration.value.t319 ?? 400,
      };
    }

    this.ongoingRequest = true;
    this.newDeploymentSubscription = this.tenantManagementService
      .createTenant(newDeployment)
      .subscribe({
        next: () => {
          this.ongoingRequest = false;
          this.tenantManagementService.refreshTenants();
          this.newDeploymentForm = this.getNewDeploymentForm();
          this.simpleMessageService.showSimpleMessage({
            message: "Deployment created successfully!",
            icon: "fa-solid fa-check-double",
            color: "success",
          });
          this.redirectToDeploymentsPage();
        },
        error: () => {
          this.ongoingRequest = false;
          this.simpleMessageService.showSimpleMessage({
            message: "Failed to create deployment!",
            icon: "fa-solid fa-triangle-exclamation",
            color: "danger",
          });
        },
      });
  }

  protected redirectToDeploymentsPage(): void {
    this.router.navigate([AvailableRoutes.ADMIN, AvailableRoutes.DEPLOYMENTS]);
  }

  ngOnDestroy(): void {
    this.newDeploymentSubscription?.unsubscribe();
  }
}
